import type { ReactElement } from "react";
import { useMemo } from "react";

import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { StyledCheckboxFilterResultContainer } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/filters/CheckboxFilter/styles";
import type { ICheckboxFilterOption } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/filters/CheckboxFilter/types";
import type { ContactDetail } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/enums";

import type { TranslatedOption } from "../../../models/ContactDetailsFilter/ContactDetailsFilter";
import type FindContactDetailsFilter from "../../../models/ContactDetailsFilter/ContactDetailsFilter";

import { mapToTranslatedOptions } from "./utils";

const ContactDetailsFilterResult = ({
    value,
    filter,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindContactDetailsFilter>): ReactElement | null => {
    const handleRemove = (id: ContactDetail): void => {
        const updatedValues = value?.filter((v: ContactDetail) => v !== id);
        onFilterUpdate(updatedValues);
        onFilterValueRemove?.(updatedValues);
    };

    const options: ICheckboxFilterOption<ContactDetail>[] = useMemo(() => {
        if (!value || value.length === 0) {
            return [];
        }

        return mapToTranslatedOptions(filter.options, "chip").filter((option: TranslatedOption) =>
            value.includes(option.value),
        );
    }, [value, filter]);

    if (!value?.length) {
        return null;
    }

    return (
        <StyledCheckboxFilterResultContainer>
            {options.map((item: ICheckboxFilterOption<ContactDetail>) => {
                const valueLifecycleStatus = onGetLifecycleStatus?.(item.value);

                return (
                    <FiltersChip
                        text={item.label}
                        chipStatus={valueLifecycleStatus}
                        hideActionButton={!isActionEnabled}
                        onClose={() => handleRemove(item.value)}
                    />
                );
            })}
        </StyledCheckboxFilterResultContainer>
    );
};

export default ContactDetailsFilterResult;
